import { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

import AvatarCard from "@/components/cards/AvatarCard";
import AvatarCardSkeleton from "@/components/cards/AvatarCard.skeleton";
import { IconSadFace } from "@/components/icons";

import { ROUTES } from "@/config/routes";
import { useGetPersons } from "@/hooks/api/content.hooks";

const PersonsPage = (): JSX.Element => {
  const { t } = useTranslation();

  const { ref: inViewRef, inView } = useInView({ threshold: 1.0 });

  const { data, fetchNextPage, hasNextPage, isFetching } = useGetPersons(50);
  const flattenedData = data?.pages.flatMap((page) => page.results) || [];

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetching]);
  return (
    <>
      <h1 className={"mt-14 text-[2.75rem]/normal font-semibold max-lg:mt-7 max-lg:text-3xl/10"}>
        {t("General.authors")}
      </h1>
      <div className={"mt-8 space-y-6"}>
        <section className={"rounded-7.5 max-lg:py-0"}>
          {flattenedData && flattenedData.length ? (
            <div className={"persons-grid"}>
              {flattenedData.map((item, index) => (
                <AvatarCard
                  key={index}
                  type={"round-1"}
                  link={ROUTES.PERSON_DETAILS.replace(":id", item.id.toString())}
                  imageSrc={item.picture.small}
                  name={item.name}
                />
              ))}
            </div>
          ) : !isFetching ? (
            <div className={"flex flex-col items-center"}>
              <IconSadFace className={"size-20 fill-grey-1"} />
              <div className={"mt-4 max-w-[27.5rem] text-center"}>
                <h1 className={"text-2xl font-semibold"}>{t("error.nothing-found")}</h1>
                <p className={"mt-2 text-lg/6 text-on-surface"}>{t("error.nothing-found-message")}</p>
              </div>
            </div>
          ) : null}
          {isFetching ? (
            <div className={"persons-grid mt-[16px]"}>
              {[...Array(20).keys()].map((_, index) => (
                <AvatarCardSkeleton key={index} type="round-1" />
              ))}
            </div>
          ) : null}
          {flattenedData.length ? <div ref={inViewRef} className={"h-[1px] w-full"} /> : null}
        </section>
      </div>
    </>
  );
};

export default PersonsPage;
